// @flow

import moment from 'moment';

import * as CommonValidators from 'Components/utils/common-validator/CommonValidators';

const { maxAge, minAge, date } = CommonValidators;

const MAX_PAX = 6;
const API_DATE_FORMAT = 'YYYYMMDD';

type CheckFormErrorsParamsType = {
  adults?: number,
  children?: number,
  departureDate?: ?string,
  birthdays?: Array<string>,
  dateFormat: string,
  adultAge: number,
  childAge?: number,
  birthdayFormatErrorMessage: string,
  adultAgeErrorMessage: string,
  minAgeErrorMessage: string,
};

type BirthdayErrorMessageParams = {
  departureDate: ?string,
  dateFormat: string,
  adultAge: number,
  childAge: number,
  birthdayFormatErrorMessage: string,
  adultAgeErrorMessage: string,
  minAgeErrorMessage: string,
  birthday: string,
};

export type FormErrorsType = {
  maxPax: boolean,
  departureDate: boolean,
  birthdays: boolean,
  birthdaysErrorMessage: string,
};

function hasTooManyPax(adults: number, children: number): boolean {
  return adults + children > MAX_PAX;
}

function getBirthDayErrorMessage(params: BirthdayErrorMessageParams): string {
  const {
    departureDate,
    birthday,
    dateFormat,
    adultAge,
    childAge,
    birthdayFormatErrorMessage,
    adultAgeErrorMessage,
    minAgeErrorMessage,
  } = params;

  const onDate = departureDate ? String(departureDate) : '';
  const nowDate = moment().format(API_DATE_FORMAT);

  const adultAgeValidator = maxAge({
    ageRequired: +adultAge,
    birthdayFormat: dateFormat,
    onDate: nowDate,
    onDateFormat: API_DATE_FORMAT,
  });

  const maxAgeValidator = maxAge({
    ageRequired: +adultAge,
    birthdayFormat: dateFormat,
    onDate,
    onDateFormat: API_DATE_FORMAT,
  });

  const minAgeValidator = minAge({
    ageRequired: +childAge,
    birthdayFormat: dateFormat,
    onDate,
    onDateFormat: API_DATE_FORMAT,
  });

  const isChildBorn = minAge({
    ageRequired: 0,
    birthdayFormat: dateFormat,
    onDate,
    onDateFormat: API_DATE_FORMAT,
  });

  const dateValidator = date(dateFormat);

  if (!dateValidator.isValid(birthday) || !adultAgeValidator.isValid(birthday)) {
    return birthdayFormatErrorMessage;
  }

  if (!isChildBorn.isValid(birthday)) {
    return birthdayFormatErrorMessage;
  }

  if (!maxAgeValidator.isValid(birthday)) {
    return adultAgeErrorMessage;
  }

  if (!minAgeValidator.isValid(birthday)) {
    return minAgeErrorMessage;
  }

  return '';
}

function checkFormErrors(params: CheckFormErrorsParamsType): FormErrorsType {
  const {
    adults = 2,
    children = 0,
    departureDate = '',
    birthdays = [],
    dateFormat,
    adultAge,
    childAge = 0,
    birthdayFormatErrorMessage,
    adultAgeErrorMessage,
    minAgeErrorMessage,
  } = params;

  const birthdaysErrorMessage = birthdays.reduce(
    (errorMessage, birthday) =>
      errorMessage ||
      getBirthDayErrorMessage({
        dateFormat,
        departureDate,
        adultAge,
        childAge,
        birthday,
        birthdayFormatErrorMessage,
        adultAgeErrorMessage,
        minAgeErrorMessage,
      }),
    null,
  );

  const birthdaysNotValid = birthdays.length !== children || !!birthdaysErrorMessage;

  return {
    maxPax: hasTooManyPax(+adults, +children),
    departureDate: !departureDate,
    birthdays: birthdaysNotValid,
    birthdaysErrorMessage:
      birthdaysErrorMessage || (birthdaysNotValid && birthdayFormatErrorMessage) || '',
  };
}

export default { checkFormErrors };
